import axios, {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { camelCase, isArray, isPlainObject, snakeCase, transform } from "lodash";
import { useAuthStore } from "@/stores/auth";
import { useCompanyStore } from "@/stores/company";

type TransformableValue = Record<string, unknown> | unknown[];

const isTransformable = (value: unknown): value is TransformableValue =>
  isArray(value) || isPlainObject(value);

const toCamelCase = (obj: TransformableValue) =>
  transform(
    obj,
    (result: Record<string, unknown>, value: unknown, key: string, target) => {
      const camelKey = isArray(target) ? key : camelCase(key);
      result[camelKey] = isTransformable(value)
        ? toCamelCase(value)
        : value;
    }
  );

const toSnakeCase = (obj: TransformableValue) =>
  transform(
    obj,
    (result: Record<string, unknown>, value: unknown, key: string, target) => {
      const camelKey = isArray(target) ? key : snakeCase(key);
      result[camelKey] = isTransformable(value)
        ? toSnakeCase(value)
        : value;
    }
  );

const client = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  timeout: Number(import.meta.env.VITE_APP_API_TIMEOUT || 3000),
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();

  config.headers = config.headers ?? {};
  if (authStore.accessToken) {
    config.headers["token"] = authStore.accessToken;
  }

  if (companyStore.currentCompanyId) {
    config.headers["company"] = companyStore.currentCompanyId;
  }

  if (config.data && isTransformable(config.data)) {
    config.data = toSnakeCase(config.data);
  }
  if (config.params && isTransformable(config.params)) {
    config.params = toSnakeCase(config.params);
  }
  return config;
});

client.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && isTransformable(response.data)) {
      response.data = toCamelCase(response.data);
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      if (error.response.status === 401) {
        const authStore = useAuthStore();
        authStore.logOut();
        window.location.href = "/auth/login";
      } else if (error.response.data && isTransformable(error.response.data)) {
        error.response.data = toCamelCase(error.response.data as any);

        if (error.response.status === 403) {
          const payload = error.response.data as {
            code?: number;
            errors?: { reason?: string; billingRequired?: boolean } | string;
          };
          const reason =
            typeof payload.errors === "object" && payload.errors
              ? payload.errors.reason
              : "";
          const billingRequired =
            payload.code === 7006 ||
            reason === "billing_required" ||
            (typeof payload.errors === "object" && Boolean(payload.errors?.billingRequired));

          if (billingRequired) {
            const currentPath = window.location.pathname;
            const isBillingPath = currentPath.startsWith("/billing");
            if (!isBillingPath) {
              window.location.href = "/billing";
            }
          }
        }
      }
    }

    throw error;
  }
);

export { client };
